import { supabase } from "@/integrations/supabase/client";
import { filterProvinces } from "@/lib/filterData";
import { municipalityCoords } from "@/lib/municipalityCoords";

export interface ScoredListing {
  listing: any;
  score: number;
}

function getProvince(municipality?: string | null): string | null {
  if (!municipality) return null;
  for (const p of filterProvinces) {
    if (p.municipalities.includes(municipality)) return p.name;
  }
  return null;
}

function haversineKm(a: [number, number], b: [number, number]): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b[0] - a[0]);
  const dLon = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(x));
}

function resolveCoords(municipality?: string | null, location?: string | null): [number, number] | null {
  if (municipality && municipalityCoords[municipality]) return municipalityCoords[municipality];
  if (location) {
    const first = location.split(",")[0].trim();
    if (municipalityCoords[first]) return municipalityCoords[first];
  }
  return null;
}

function geoScore(base: any, cand: any): number {
  const baseMuni = base.municipality ?? null;
  const candMuni = cand.municipality ?? null;
  if (baseMuni && candMuni && baseMuni === candMuni) return 18;

  const baseProv = getProvince(baseMuni);
  const candProv = getProvince(candMuni);
  if (baseProv && candProv && baseProv === candProv) return 12;

  const baseCoords = resolveCoords(baseMuni, base.location);
  const candCoords = resolveCoords(candMuni, cand.location);
  if (baseCoords && candCoords) {
    const km = haversineKm(baseCoords, candCoords);
    if (km <= 50) return 8;
    if (km <= 150) return 4;
  }
  return 0;
}

function priceScore(base: any, cand: any): number {
  const bp = Number(base.price);
  const cp = Number(cand.price);
  if (!bp || !cp || bp <= 0 || cp <= 0) return 0;
  const diff = Math.abs(bp - cp) / bp;
  if (diff <= 0.1) return 8;
  if (diff <= 0.25) return 5;
  if (diff <= 0.5) return 2;
  return 0;
}

export function scoreListing(base: any, cand: any): number {
  let score = 40; // category guaranteed by query

  if (base.sub_category && cand.sub_category && base.sub_category === cand.sub_category) {
    score += 20;
  }

  if (base.listing_type && cand.listing_type && base.listing_type === cand.listing_type) {
    score += 30;
  }

  if (
    base.species &&
    cand.species &&
    base.species.toLowerCase() === cand.species.toLowerCase()
  ) {
    score += 20;
  }

  score += geoScore(base, cand);

  const lt = (base.listing_type ?? base.category ?? "").toLowerCase();
  if (lt === "plankor" || lt === "ved") {
    if (
      base.moisture_content &&
      cand.moisture_content &&
      base.moisture_content === cand.moisture_content
    ) {
      score += 12;
    }
  }
  if (lt === "plankor") {
    if (
      base.processing_grade &&
      cand.processing_grade &&
      base.processing_grade === cand.processing_grade
    ) {
      score += 12;
    }
  }

  score += priceScore(base, cand);
  return score;
}

export async function fetchSimilarListings(base: any, limit: number): Promise<any[]> {
  const { data, error } = await supabase
    .from("listings")
    .select("*")
    .eq("status", "aktiv")
    .eq("category", base.category)
    .neq("id", base.id)
    .limit(50);
  if (error) throw error;
  const candidates = data ?? [];
  const scored: ScoredListing[] = candidates.map((c) => ({
    listing: c,
    score: scoreListing(base, c),
  }));
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.listing);
}
